import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  TOPICS, POPULAR_ARTICLES, searchHelp, articleById, articlesByTopic, topicById,
  type HelpArticle,
} from '@/lib/helpCenter';
import {
  SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL, SUPPORT_EMAIL, SUPPORT_HOURS, supportMailto,
} from '@/lib/support';

/**
 * Souqli Help Centre.
 *
 * Smart, self-service support modelled on Amazon and Vinted: a plain-language
 * search that ranks the knowledge base instantly (no API), "popular" topics
 * surfaced up front, task tiles to browse, full articles, and a clear contact
 * block (phone, email, and per-order help) as the fallback.
 *
 * State lives in the URL (?q= search, ?a= article, ?t= topic) so results and
 * articles are shareable and the back button just works.
 */
export default function Help() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const aId = params.get('a');
  const tId = params.get('t');

  const [query, setQuery] = useState(q);
  useEffect(() => { setQuery(q); }, [q]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [aId, tId]);

  const results = useMemo(() => (query.trim() ? searchHelp(query) : []), [query]);
  const article = aId ? articleById(aId) : undefined;
  const topic = tId ? topicById(tId) : undefined;

  function onSearch(v: string) {
    setQuery(v);
    const next = new URLSearchParams();
    if (v.trim()) next.set('q', v);
    setParams(next, { replace: true });
  }
  const openArticle = (id: string) => setParams({ a: id });
  const openTopic = (id: string) => setParams({ t: id });
  const clearAll = () => { setQuery(''); setParams({}); };

  const searching = query.trim().length > 0;

  return (
    <div className="bg-cream min-h-[70vh]">
      {/* ── Hero + smart search ── */}
      <section className="section-dark relative text-cream py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 55%,#0F0F12)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
        <div className="relative max-w-[820px] mx-auto px-6 text-center">
          <div className="stamp text-gold text-[10px]">HELP CENTRE</div>
          <h1 className="display text-4xl md:text-5xl mt-2">How can we help?</h1>
          <p className="text-cream/60 text-sm mt-3 max-w-[52ch] mx-auto">
            Search for an answer, browse a topic, or reach our team. Most questions are answered right here.
          </p>

          <div className="mt-7 relative max-w-[560px] mx-auto">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-mute text-lg" aria-hidden>🔍</span>
            <input
              autoFocus
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Try “where is my order” or “how do I get paid”"
              aria-label="Search the Help Centre"
              className="w-full rounded-luxe border border-warmline bg-white text-ink pl-11 pr-10 py-3.5 text-[15px] shadow-luxe focus:outline-none focus:ring-2 focus:ring-gold/50"
            />
            {searching && (
              <button onClick={clearAll} aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-mute hover:text-ink text-lg">×</button>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-[980px] mx-auto px-6 py-12">
        {/* ── Search results ── */}
        {searching ? (
          <SearchResults query={query} results={results} onOpen={openArticle} />
        ) : article ? (
          <ArticleView article={article} onOpen={openArticle} onTopic={openTopic} onBack={clearAll} />
        ) : topic ? (
          <TopicView topicId={topic.id} onOpen={openArticle} onBack={clearAll} />
        ) : (
          <>
            {/* Popular / anticipated */}
            <div className="mb-10">
              <div className="stamp text-gold-dark text-[10px] mb-3">POPULAR RIGHT NOW</div>
              <div className="flex flex-wrap gap-2">
                {POPULAR_ARTICLES.map((a) => (
                  <button key={a.id} onClick={() => openArticle(a.id)}
                    className="pill bg-white border border-warmline text-ink hover:border-gold hover:text-gold-dark transition text-[12.5px] px-3.5 py-2">
                    {a.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Topic tiles */}
            <div className="stamp text-gold-dark text-[10px] mb-3">BROWSE BY TOPIC</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TOPICS.map((t) => (
                <button key={t.id} onClick={() => openTopic(t.id)}
                  className="text-left bg-white border border-warmline rounded-luxe p-5 shadow-luxe hover:-translate-y-0.5 hover:shadow-frame hover:border-gold/40 transition">
                  <div className="text-2xl">{t.icon}</div>
                  <div className="display text-[18px] text-ink mt-2">{t.title}</div>
                  <div className="text-[13px] text-mute mt-1 leading-snug">{t.blurb}</div>
                  <div className="text-[12px] text-gold-dark mt-3">{articlesByTopic(t.id).length} articles →</div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Contact block (always available) ── */}
        <ContactBlock />
      </div>
    </div>
  );
}

/* ─────────────────────────── Search results ─────────────────────────── */

function SearchResults({ query, results, onOpen }: {
  query: string; results: ReturnType<typeof searchHelp>; onOpen: (id: string) => void;
}) {
  return (
    <div>
      <div className="text-[13px] text-mute mb-4">
        {results.length > 0
          ? <>Showing {results.length} result{results.length > 1 ? 's' : ''} for <span className="text-ink font-medium">“{query}”</span></>
          : <>No direct matches for <span className="text-ink font-medium">“{query}”</span>. Try different words, or contact us below.</>}
      </div>

      <div className="space-y-3">
        {results.map(({ article }) => (
          <button key={article.id} onClick={() => onOpen(article.id)}
            className="w-full text-left bg-white border border-warmline rounded-luxe p-5 shadow-luxe hover:border-gold/50 hover:-translate-y-0.5 transition">
            <div className="flex items-center gap-2">
              <span aria-hidden>{topicById(article.topic)?.icon}</span>
              <span className="display text-[17px] text-ink">{article.title}</span>
            </div>
            <div className="text-[13px] text-mute mt-1 leading-snug">{article.summary}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Topic view ─────────────────────────── */

function TopicView({ topicId, onOpen, onBack }: {
  topicId: string; onOpen: (id: string) => void; onBack: () => void;
}) {
  const topic = topicById(topicId);
  const items = articlesByTopic(topicId);
  if (!topic) return null;
  return (
    <div>
      <button onClick={onBack} className="text-[13px] text-gold-dark hover:underline mb-4">← All topics</button>
      <div className="flex items-center gap-3 mb-1">
        <span className="text-3xl" aria-hidden>{topic.icon}</span>
        <h2 className="display text-3xl text-ink">{topic.title}</h2>
      </div>
      <p className="text-[14px] text-mute mb-6">{topic.blurb}</p>
      <div className="space-y-3">
        {items.map((a) => (
          <button key={a.id} onClick={() => onOpen(a.id)}
            className="w-full text-left bg-white border border-warmline rounded-luxe p-5 shadow-luxe hover:border-gold/50 hover:-translate-y-0.5 transition">
            <div className="display text-[17px] text-ink">{a.title}</div>
            <div className="text-[13px] text-mute mt-1 leading-snug">{a.summary}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Article view ─────────────────────────── */

function ArticleView({ article, onOpen, onTopic, onBack }: {
  article: HelpArticle; onOpen: (id: string) => void; onTopic: (id: string) => void; onBack: () => void;
}) {
  const [rated, setRated] = useState<null | 'yes' | 'no'>(null);
  const topic = topicById(article.topic);
  const related = articlesByTopic(article.topic).filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <article>
      <nav className="text-[12.5px] text-mute mb-4 flex items-center gap-1.5 flex-wrap">
        <button onClick={onBack} className="text-gold-dark hover:underline">Help Centre</button>
        <span>/</span>
        {topic && <button onClick={() => onTopic(topic.id)} className="text-gold-dark hover:underline">{topic.title}</button>}
      </nav>

      <div className="bg-white border border-warmline rounded-luxe shadow-luxe p-6 sm:p-8">
        <h1 className="display text-2xl sm:text-3xl text-ink">{article.title}</h1>
        <div className="mt-4 space-y-3 text-[14.5px] text-ink/80 leading-relaxed">
          {article.body.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {article.steps && (
          <ol className="mt-5 space-y-2.5">
            {article.steps.map((s, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-ink/85">
                <span className="shrink-0 w-6 h-6 rounded-full bg-gold/15 text-gold-dark grid place-items-center text-[12px] font-bold">{i + 1}</span>
                <span className="pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        )}

        {article.link && (
          <Link to={article.link.to} className="btn btn-primary mt-6 inline-flex">{article.link.label} →</Link>
        )}

        {/* Was this helpful? */}
        <div className="mt-8 pt-5 border-t border-warmline">
          {rated === null ? (
            <div className="flex items-center gap-3">
              <span className="text-[13px] text-mute">Was this helpful?</span>
              <button onClick={() => { setRated('yes'); toast.success('Glad that helped!'); }}
                className="btn btn-outline btn-sm">👍 Yes</button>
              <button onClick={() => { setRated('no'); toast('No worries, our team can help below.', { icon: '💬' }); }}
                className="btn btn-outline btn-sm">👎 No</button>
            </div>
          ) : rated === 'yes' ? (
            <div className="text-[13px] text-mute">Thanks for the feedback.</div>
          ) : (
            <div className="text-[13px] text-mute">Sorry this did not solve it. Reach our team using the options below.</div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-8">
          <div className="stamp text-gold-dark text-[10px] mb-3">RELATED</div>
          <div className="space-y-2.5">
            {related.map((a) => (
              <button key={a.id} onClick={() => onOpen(a.id)}
                className="w-full text-left bg-white border border-warmline rounded-luxe p-4 hover:border-gold/50 transition">
                <span className="text-[14px] text-ink">{a.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

/* ─────────────────────────── Contact block ─────────────────────────── */

function ContactBlock() {
  return (
    <div className="mt-14">
      <div className="text-center mb-6">
        <div className="stamp text-gold-dark text-[10px]">STILL NEED A HAND?</div>
        <h2 className="display text-2xl text-ink mt-1">Contact Souqli support</h2>
        <p className="text-[13px] text-mute mt-1">{SUPPORT_HOURS}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {/* Phone */}
        <div className="bg-white border border-warmline rounded-luxe p-5 text-center">
          <div className="text-2xl">📞</div>
          <div className="display text-[16px] text-ink mt-2">Call us</div>
          <a href={`tel:${SUPPORT_PHONE_TEL}`} className="block text-[15px] text-gold-dark font-medium mt-1 hover:underline">
            {SUPPORT_PHONE_DISPLAY}
          </a>
          <p className="text-[12px] text-mute mt-1">{SUPPORT_HOURS}</p>
        </div>

        {/* Email */}
        <div className="bg-white border border-warmline rounded-luxe p-5 text-center">
          <div className="text-2xl">✉️</div>
          <div className="display text-[16px] text-ink mt-2">Email us</div>
          <a href={supportMailto('Support request')} className="block text-[13.5px] text-gold-dark mt-1 hover:underline break-all">
            {SUPPORT_EMAIL}
          </a>
          <Link to="/contact" className="text-[12px] text-mute mt-1 inline-block hover:text-ink">or use the contact form →</Link>
        </div>

        {/* In-order help */}
        <div className="bg-white border border-warmline rounded-luxe p-5 text-center">
          <div className="text-2xl">📦</div>
          <div className="display text-[16px] text-ink mt-2">Help with an order</div>
          <p className="text-[12px] text-mute mt-1">Fastest for delivery, refund or dispute issues; it shares your order details with us.</p>
          <Link to="/dashboard/orders" className="btn btn-outline btn-sm mt-2">Get help with an order</Link>
        </div>
      </div>
    </div>
  );
}
